import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { useMemo, useCallback } from "react";

import { AccommodationFacilityType } from "../../../../../../../__generated__/gql/graphql";

/** URL パラメータ名 */
const PARAM_NAME = "facility-filter";
/** URL パラメータのセパレータ */
const PARAM_SEPARATOR = "-";

/**
 * 施設フィルタアイテムの定義
 */
export const facilityFilterItems: {
  type: AccommodationFacilityType;
  label: string;
  icon: string;
}[] = [
  {
    type: AccommodationFacilityType.Sauna,
    label: "サウナ",
    icon: "/i/icon_sauna.svg",
  },
  {
    type: AccommodationFacilityType.Onsen,
    label: "温泉",
    icon: "/i/icon_onsen.svg",
  },
  {
    type: AccommodationFacilityType.Workation,
    label: "ワーケーション",
    icon: "/i/icon_workation.svg",
  },
  {
    type: AccommodationFacilityType.DogFriendly,
    label: "ドッグフレンドリー",
    icon: "/i/icon_dogfriendly.svg",
  },
  {
    type: AccommodationFacilityType.EvCharger,
    label: "EV充電器",
    icon: "/i/icon_evstation.svg",
  },
];

// nuqs parser for facility filter
const facilityFilterParser = parseAsArrayOf(
  parseAsString,
  PARAM_SEPARATOR,
).withDefault([]);

/**
 * 宿泊施設がフィルタ条件に一致するかどうかを判定（AND検索）
 *
 * フィルタが空の場合は全て一致。
 * フィルタに含まれる全ての設備を宿泊施設が持っている場合のみ一致。
 */
function matchesFacility(
  facilityTypes: AccommodationFacilityType[],
  filter: ReadonlySet<AccommodationFacilityType>,
): boolean {
  if (filter.size === 0) return true;
  const accommodationSet = new Set(facilityTypes);
  for (const required of filter) {
    if (!accommodationSet.has(required)) return false;
  }
  return true;
}

/**
 * 施設フィルタの状態管理とURL同期を行うカスタムフック
 *
 * - nuqs を使用して URL パラメータと状態を同期
 * - AccommodationFacilityType のみを受け入れる
 */
export function useFacilityFilter() {
  const [urlFilter, setUrlFilter] = useQueryState(
    PARAM_NAME,
    facilityFilterParser,
  );

  // URL の文字列配列を Set<AccommodationFacilityType> に変換
  const facilityFilter = useMemo(() => {
    const validTypes = urlFilter.filter(
      (param): param is AccommodationFacilityType =>
        Object.values(AccommodationFacilityType).includes(
          param as AccommodationFacilityType,
        ),
    );
    return new Set(validTypes);
  }, [urlFilter]);

  const setFacilityFilter = useCallback(
    (filter: Set<AccommodationFacilityType>) => {
      const filterArray = Array.from(filter);
      void setUrlFilter(filterArray.length > 0 ? filterArray : []);
    },
    [setUrlFilter],
  );

  const matchesFacilityFilter = useCallback(
    (facilityTypes: AccommodationFacilityType[]): boolean => {
      return matchesFacility(facilityTypes, facilityFilter);
    },
    [facilityFilter],
  );

  return {
    /** 現在のフィルタ状態 */
    facilityFilter,
    /** フィルタ更新関数（状態とURLを同期更新） */
    setFacilityFilter,
    /** 宿泊施設がフィルタ条件に一致するかを判定 */
    matchesFacilityFilter,
  };
}
